import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import type { TicketAttachment } from "@/lib/support";
import { attachmentLabel, attachmentUrl, isTicketAttachmentImage } from "@/lib/support";
import { useDialogDismissGuard } from "@/components/ui/useDialogDismissGuard";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { useModalStackLayer } from "@/lib/modalStack";

export function SupportAttachments({
  attachments,
  variant = "grid",
  title = "Anhänge",
  emptyText = "Keine Anhänge vorhanden.",
  showTitle = true,
  className,
}: {
  attachments: TicketAttachment[];
  variant?: "grid" | "list";
  title?: string;
  emptyText?: string;
  showTitle?: boolean;
  className?: string;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const images = useMemo(
    () => attachments.filter(isTicketAttachmentImage),
    [attachments],
  );
  const files = useMemo(
    () => attachments.filter((a) => !isTicketAttachmentImage(a)),
    [attachments],
  );

  if (!attachments.length) {
    if (!showTitle) return null;
    return (
      <section className={className}>
        {showTitle ? <h3 className="text-sm font-semibold text-label">{title}</h3> : null}
        <p className="mt-1 text-sm text-label-tertiary">{emptyText}</p>
      </section>
    );
  }

  return (
    <section className={className}>
      {showTitle ? (
        <h3
          className={clsx(
            "font-semibold text-label",
            variant === "grid" ? "text-xs uppercase tracking-wide text-label-tertiary" : "text-sm",
          )}
        >
          {title}
        </h3>
      ) : null}

      {images.length ? (
        <ul
          className={clsx(
            "mt-2 grid gap-2",
            variant === "grid" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-3",
          )}
        >
          {images.map((att, index) => {
            const label = attachmentLabel(att);
            const url = attachmentUrl(att);
            return (
              <li key={att.id}>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="group relative block w-full overflow-hidden rounded-ios border border-separator bg-fill-quaternary/20 text-left transition hover:border-accent/40 hover:ring-2 hover:ring-accent/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  title={`${label} vergrößern`}
                >
                  <img
                    src={url}
                    alt={label}
                    loading="lazy"
                    crossOrigin="use-credentials"
                    className="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.02]"
                  />
                  <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-6 text-[11px] font-medium text-white">
                    {label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {files.length ? (
        <ul
          className={clsx(
            "text-sm",
            images.length ? "mt-3" : "mt-2",
            variant === "list" ? "list-disc space-y-1 pl-5" : "grid gap-2 sm:grid-cols-2",
          )}
        >
          {files.map((att) => {
            const label = attachmentLabel(att);
            const url = attachmentUrl(att);
            return (
              <li key={att.id}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener"
                  className={clsx(
                    variant === "list"
                      ? "text-accent hover:underline"
                      : "flex items-center gap-2 rounded-ios border border-separator bg-fill-quaternary/15 px-3 py-2 text-accent hover:bg-fill-tertiary",
                  )}
                >
                  {variant === "grid" ? <span aria-hidden>📎</span> : null}
                  <span className={variant === "grid" ? "truncate" : undefined}>{label}</span>
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}

      <SupportImageLightbox
        images={images}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onChangeIndex={setLightboxIndex}
      />
    </section>
  );
}

function SupportImageLightbox({
  images,
  index,
  onClose,
  onChangeIndex,
}: {
  images: TicketAttachment[];
  index: number | null;
  onClose: () => void;
  onChangeIndex: (index: number | null) => void;
}) {
  const open = index != null && index >= 0 && index < images.length;
  const current = open ? images[index] : null;
  const url = current ? attachmentUrl(current) : null;
  const label = current ? attachmentLabel(current) : "";
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();
  const guardedClose = useDialogDismissGuard(open, onClose);
  const { zIndex: stackZIndex } = useModalStackLayer(open);

  const showPrev = open && images.length > 1 && index! > 0;
  const showNext = open && images.length > 1 && index! < images.length - 1;

  const goPrev = useCallback(() => {
    if (index == null || index <= 0) return;
    onChangeIndex(index - 1);
  }, [index, onChangeIndex]);

  const goNext = useCallback(() => {
    if (index == null || index >= images.length - 1) return;
    onChangeIndex(index + 1);
  }, [index, images.length, onChangeIndex]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, goPrev, goNext]);

  return (
    <>
      <Transition show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[200]"
          style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
          onClose={guardedClose}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/90" aria-hidden />
          </TransitionChild>

          <div className="fixed inset-0 flex flex-col items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative flex max-h-full max-w-full flex-col items-center">
                {url ? (
                  <img
                    src={url}
                    alt={label}
                    crossOrigin="use-credentials"
                    className="max-h-[82vh] max-w-[min(95vw,1200px)] rounded-ios-lg object-contain shadow-ios-md"
                    onContextMenu={(e) => {
                      const imageElement =
                        e.target instanceof HTMLImageElement ? e.target : null;
                      openMediaContextMenu(e, {
                        url,
                        filename: label || "bild.jpg",
                        contentType: current?.content_type || "image/jpeg",
                        isImage: true,
                        imageElement,
                      });
                    }}
                  />
                ) : null}

                <p className="mt-3 max-w-[min(95vw,1200px)] truncate text-center text-sm text-white/90">
                  {label}
                  {images.length > 1 && index != null ? (
                    <span className="text-white/60"> · {index + 1} / {images.length}</span>
                  ) : null}
                </p>

                <button
                  type="button"
                  className="absolute -right-2 -top-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-card text-label shadow-ios"
                  onClick={onClose}
                  aria-label="Schließen"
                >
                  ✕
                </button>

                {showPrev ? (
                  <button
                    type="button"
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 rounded-full bg-card/95 px-3 py-2 text-lg text-label shadow-ios sm:-translate-x-12"
                    onClick={goPrev}
                    aria-label="Vorheriges Bild"
                  >
                    ‹
                  </button>
                ) : null}

                {showNext ? (
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 rounded-full bg-card/95 px-3 py-2 text-lg text-label shadow-ios sm:translate-x-12"
                    onClick={goNext}
                    aria-label="Nächstes Bild"
                  >
                    ›
                  </button>
                ) : null}
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
      {contextMenuNode}
    </>
  );
}
